import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import CroppedImage, { getCroppedAspectRatio, getImageUrlsForCrop } from '@components/shared/CroppedImage';
import useImageCap from '@/hooks/useImageCap';

/**
 * ImageCycler Component
 * 
 * Cycles through multiple images in a single tile for directory galleries.
 * Each image can have its own crop applied.
 * Images switch instantly (no transition animation) on an interval.
 */
function ImageCycler({ 
    imageIds, 
    cycleDuration = 3000, 
    crops = {}, 
    imageData = {},
    className = '',
    aspectRatio = null,
    showCycleCounter = false,
    // Off by default: directory tiles have always swapped instantly, and turning
    // that into a crossfade everywhere would change how every existing directory
    // page behaves. It is chosen per gallery block.
    crossfade = false,
}) {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [isLoaded, setIsLoaded] = React.useState({});
    const imgRef = React.useRef(null);
    const preloadRefs = React.useRef({});
    
    // Cycle through images on interval — only after all tile images have loaded
    const allLoaded = imageIds?.length > 0 && imageIds.every((id) => !!isLoaded[id]);
    React.useEffect(() => {
        if (!imageIds || imageIds.length <= 1 || !allLoaded) return;

        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % imageIds.length);
        }, cycleDuration);

        return () => clearInterval(interval);
    }, [imageIds, cycleDuration, allLoaded]);
    
    // Reset index if imageIds change
    React.useEffect(() => {
        setCurrentIndex(0);
    }, [imageIds?.length]);

    const currentImageId = imageIds?.[currentIndex];
    const currentImage = imageData?.[currentImageId];

    // URL/srcset map for preload images
    const imageCap = useImageCap();
    const imageUrlMap = React.useMemo(() => {
        const map = {};
        for (const id of imageIds || []) {
            const file = imageData?.[id];
            if (file) map[id] = getImageUrlsForCrop(file, crops?.[id], 600, imageCap);
        }
        return map;
    }, [imageIds, imageData, crops, imageCap]);

    const handleImageLoad = (imageId) => {
        setIsLoaded(prev => ({ ...prev, [imageId]: true }));
    };

    // If image is already complete (e.g. cached) when we mount, onLoad may have fired before listener attached — treat as loaded
    React.useEffect(() => {
        const el = imgRef.current;
        if (!el) return;
        const check = () => {
            if (el.complete && !isLoaded[currentImageId]) {
                handleImageLoad(currentImageId);
            }
        };
        check();
        const t = setTimeout(check, 50);
        return () => clearTimeout(t);
    }, [currentImageId, isLoaded, handleImageLoad]);

    // Preload imgs may be cached; check complete and mark loaded so cycling can start
    React.useEffect(() => {
        const preloadIds = imageIds?.length > 1 ? imageIds.slice(1) : [];
        const check = () => {
            for (const id of preloadIds) {
                const el = preloadRefs.current[id];
                if (el?.complete && !isLoaded[id]) handleImageLoad(id);
            }
        };
        check();
        const t = setTimeout(check, 50);
        return () => clearTimeout(t);
    }, [imageIds, isLoaded, handleImageLoad]);

    if (!imageIds || imageIds.length === 0 || !imageData) {
        return (
            <div 
                className={cx('bg-gray-200', className)}
                style={{ aspectRatio: aspectRatio || '1 / 1' }}
            />
        );
    }

    if (!currentImage) {
        return (
            <div 
                className={cx('bg-gray-200', className)}
                style={{ aspectRatio: aspectRatio || '1 / 1' }}
            />
        );
    }

    const crop = crops[currentImageId];
    const calculatedAspectRatio = aspectRatio || (
        crop 
            ? getCroppedAspectRatio(currentImage.dimensions, crop)
            : (currentImage.dimensions?.width && currentImage.dimensions?.height
                ? `${currentImage.dimensions.width} / ${currentImage.dimensions.height}`
                : '1 / 1')
    );

    return (
        <div 
            className={cx('relative overflow-hidden', className)}
            style={{ aspectRatio: calculatedAspectRatio }}
        >
            {/* Placeholder */}
            <div 
                className={cx(
                    'absolute inset-0 transition-opacity duration-300',
                    isLoaded[currentImageId] ? 'opacity-0' : 'opacity-100'
                )}
                style={{ backgroundColor: 'color-mix(in srgb, var(--color-text) 6%, transparent)' }}
                aria-hidden="true"
            />

            {/* Preload non-current images so all are loaded before cycling starts */}
            {imageIds.length > 1 && imageIds.slice(1).map((preloadId) => {
                const urls = imageUrlMap[preloadId];
                if (!urls?.src) return null;
                const src = urls.src.startsWith('/') ? urls.src : `/${urls.src}`;
                return (
                    <img
                        key={preloadId}
                        ref={(el) => {
                            if (el) preloadRefs.current[preloadId] = el;
                            else delete preloadRefs.current[preloadId];
                        }}
                        src={src}
                        srcSet={urls.srcsetString || undefined}
                        sizes={urls.srcsetString ? urls.sizes : undefined}
                        alt=""
                        loading="eager"
                        className="absolute w-px h-px opacity-0 overflow-hidden pointer-events-none"
                        style={{ left: -9999 }}
                        onLoad={() => handleImageLoad(preloadId)}
                    />
                );
            })}
            
            {/* Current Image */}
            <CroppedImage
                ref={imgRef}
                key={currentImageId}
                image={currentImage}
                crop={crop}
                alt={currentImage.description || ''}
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                loading="eager"
                priority
                onLoad={() => handleImageLoad(currentImageId)}
                className={cx(
                    crossfade ? 'transition-opacity duration-700 ease-in-out' : 'transition-opacity duration-100',
                    isLoaded[currentImageId] ? 'opacity-100' : 'opacity-0'
                )}
            />

            {/* Cycle counter (e.g. 1/3, 2/3) when showCycleCounter and 2+ images */}
            {showCycleCounter && imageIds.length > 1 && (
                <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded tabular-nums z-10">
                    {currentIndex + 1} / {imageIds.length}
                </div>
            )}
        </div>
    );
}

ImageCycler.propTypes = {
    /** Array of image IDs to cycle through */
    imageIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    /** Duration in milliseconds between image changes (default: 3000) */
    cycleDuration: PropTypes.number,
    /** Crop data object keyed by image ID { [imageId]: { x, y, width, height } } */
    crops: PropTypes.object,
    /** Image data object keyed by image ID { [imageId]: { url, dimensions, description, ... } } */
    imageData: PropTypes.object.isRequired,
    /** Additional CSS classes */
    className: PropTypes.string,
    /** Override aspect ratio (CSS aspect-ratio value like "16 / 9") */
    aspectRatio: PropTypes.string,
    /** When true, show cycling counter (e.g. 1/3, 2/3) in bottom-right */
    showCycleCounter: PropTypes.bool,
    /** When true, fade between images instead of swapping instantly */
    crossfade: PropTypes.bool,
};

export default ImageCycler;
